import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import Loader from "../../Components/Loader";
import TableContainer from "../../Components/TableContainer";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";

const CommentReasons = () => {
    const pageSize = 15;
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [reasonData, setReasonData] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [useDummyData, setUseDummyData] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingReason, setEditingReason] = useState(null);
    const [formData, setFormData] = useState({ reason: "" });
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedReason, setSelectedReason] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Dummy fallback data
    const dummyData = [
        {
            id: 1,
            reason: "Spam or misleading",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        },
        {
            id: 2,
            reason: "Hate speech or symbols",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        },
        {
            id: 3,
            reason: "Harassment or bullying",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        },
        {
            id: 4,
            reason: "Inappropriate content",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        },
        {
            id: 5,
            reason: "Violence or dangerous behavior",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        },
        {
            id: 6,
            reason: "Copyright infringement",
            status: 1,
            created_at: "2025-12-18T18:26:50.000Z",
            updated_at: "2025-12-18T18:26:50.000Z"
        }
    ];

    const getCommentReasonsList = async () => {
        setLoading(true);
        try {
            const skip = page * pageSize;
            const params = {
                select: "id,reason",
                from: "comment_report_reasons",
                arg: "status = 1",
                skip: skip,
                limit: pageSize,
                total: 1,
            };
            const response = await AxiosInstance.post(ApiList.USER_GET, params);
            if (response?.data?.s === 1 && response?.data?.r?.length > 0) {
                const formattedReasons = response?.data?.r?.map((reason, index) => ({
                    rowid: index + 1 + page * pageSize,
                    ...reason,
                }));
                setReasonData(formattedReasons);
                setUseDummyData(false);
                if (skip === 0) {
                    const totalCount = response?.data?.total || 0;
                    localStorage.setItem("commentReasonsTotalCount", totalCount);
                    setTotalData(totalCount);
                } else {
                    const totalCountFromStorage = localStorage.getItem("commentReasonsTotalCount");
                    setTotalData(totalCountFromStorage ? Number(totalCountFromStorage) : 0);
                }
            } else {
                // Use dummy data if no data from API
                setUseDummyData(true);
                const start = page * pageSize;
                const end = start + pageSize;
                const formattedDummyData = dummyData.slice(start, end).map((reason, index) => ({
                    rowid: index + 1 + page * pageSize,
                    ...reason,
                }));
                setReasonData(formattedDummyData);
                setTotalData(dummyData.length);
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching comment reasons data:", error);
            // Use dummy data on error
            setUseDummyData(true);
            const start = page * pageSize;
            const end = start + pageSize;
            const formattedDummyData = dummyData.slice(start, end).map((reason, index) => ({
                rowid: index + 1 + page * pageSize,
                ...reason,
            }));
            setReasonData(formattedDummyData);
            setTotalData(dummyData.length);
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleAddReason = () => {
        setEditingReason(null);
        setFormData({ reason: "" });
        setOpenDialog(true);
    };

    const handleEditReason = (reason) => {
        setEditingReason(reason);
        setFormData({ reason: reason.reason });
        setOpenDialog(true);
        setAnchorEl(null);
    };

    const handleDeleteReason = async (reasonId) => {
        if (deleting) return; // Prevent multiple calls
        
        setDeleting(true);
        try {
            const formData = new FormData();
            formData.append('reason', selectedReason.reason);
            formData.append('status', '0'); // 0 for delete
            
            const response = await AxiosInstance.post(`${ApiList.EDIT_COMMENT_REASON}/${reasonId}`, formData);
            if (response?.data?.s === 1) {
                toast.success("Comment reason deleted successfully");
                getCommentReasonsList(); // Refresh the list
            } else {
                toast.error("Failed to delete comment reason");
            }
        } catch (error) {
            console.error("Error deleting comment reason:", error);
            toast.error("Error deleting comment reason");
        } finally {
            setDeleting(false);
        }
        setAnchorEl(null);
    };

    const handleSaveReason = async () => {
        if (saving) return; // Prevent multiple calls
        
        if (!formData.reason.trim()) {
            toast.error("Please enter a reason");
            return;
        }

        setSaving(true);
        try {
            const submitData = new FormData();
            
            if (editingReason) {
                // Update existing reason
                submitData.append('reason', formData.reason);
                submitData.append('status', '1');
                
                const response = await AxiosInstance.post(`${ApiList.EDIT_COMMENT_REASON}/${editingReason.id}`, submitData);
                if (response?.data?.s === 1) {
                    toast.success("Comment reason updated successfully");
                    getCommentReasonsList();
                    setOpenDialog(false);
                } else {
                    toast.error("Failed to update comment reason");
                }
            } else {
                // Add new reason
                submitData.append('reason', formData.reason);
                
                const response = await AxiosInstance.post(ApiList.ADD_COMMENT_REASON, submitData);
                if (response?.data?.s === 1) {
                    toast.success("Comment reason added successfully");
                    getCommentReasonsList();
                    setOpenDialog(false);
                } else {
                    toast.error("Failed to add comment reason");
                }
            }
        } catch (error) {
            console.error("Error saving comment reason:", error);
            toast.error("Error saving comment reason");
        } finally {
            setSaving(false);
        }
    };

    const handleMenuClick = (event, reason) => {
        setAnchorEl(event.currentTarget);
        setSelectedReason(reason);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedReason(null);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        getCommentReasonsList();
    }, [page]);

    const columns = [
        {
            field: "rowid",
            headerName: "Sr.No",
            width: 80,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "reason",
            headerName: "Reason",
            flex: 1,
            minWidth: 300,
            headerClassName: "leftAlignHeader",
            cellClassName: "leftAlign",
            renderCell: (params) => {
                const reason = params?.row?.reason;
                const truncatedReason =
                    reason?.length > 50 ? `${reason.substring(0, 50)}...` : reason;
                return (
                    <Tooltip title={reason}>
                        {truncatedReason}
                    </Tooltip>
                );
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, params.row)}
                    sx={{ color: "#000" }}
                >
                    <MoreVertIcon />
                </IconButton>
            ),
        },
    ];

    return (
        <Box
            sx={{
                width: "100%",
                height: "100vh",
                background: "#B9CC0310",
                overflow: "hidden",
            }}
        >
            <Box sx={{ width: "100%", p: 2, background: "#B9CC0310", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                    sx={{
                        fontSize: { xs: "18px", md: "28px" },
                        fontWeight: "300",
                        color: "#000",
                    }}
                >
                    Comment Reasons
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddReason}
                    sx={{
                        background: "#B9CC03",
                        color: "black",
                        fontWeight: "600",
                        "&:hover": {
                            background: "#b5ee00",
                        },
                    }}
                >
                    Add Reason
                </Button>
            </Box>
            <Box sx={{ width: "100%", height: "calc(100vh - 74px)" }}>
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        p: 1,
                        overflow: "scroll",
                    }}
                >
                    <TableContainer
                        columns={columns}
                        rows={reasonData}
                        handlePageChange={handlePageChange}
                        page={page}
                        loading={loading}
                        totalData={totalData}
                        pageSize={pageSize}
                    />
                </Box>
            </Box>

            {/* Add/Edit Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={saving ? undefined : () => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        background: "#fff",
                        color: "white",
                    }
                }}
            >
                <DialogTitle sx={{ color: "#000", borderBottom: "1px solid #00000020" }}>
                    {editingReason ? "Edit Comment Reason" : "Add New Comment Reason"}
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Reason"
                        value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                        margin="normal"
                        multiline
                        rows={3}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                color: "#000",
                                "& fieldset": { borderColor: "#000" },
                                "&:hover fieldset": { borderColor: "#000" },
                                "&.Mui-focused fieldset": { borderColor: "#000" },
                            },
                            "& .MuiInputLabel-root": { color: "#000" },
                            "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: "1px solid #00000020" }}>
                    <Button 
                        onClick={() => setOpenDialog(false)}
                        disabled={saving}
                        sx={{ color: saving ? "#999999" : "#000" }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSaveReason}
                        variant="contained"
                        disabled={saving}
                        sx={{
                            background: saving ? "#999999" : "#c6ff00",
                            color: "black",
                            fontWeight: "600",
                            "&:hover": {
                                background: saving ? "#999999" : "#b5ee00",
                            },
                        }}
                    >
                        {saving ? "Saving..." : (editingReason ? "Update" : "Add")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        background: "#fff",
                        color: "white",
                        border: "1px solid #FFFFFF20",
                    }
                }}
            >
                <MenuItem 
                    onClick={() => handleEditReason(selectedReason)}
                    sx={{ color: "white", "&:hover": { background: "#FFFFFF10" } }}
                >
                    <EditIcon sx={{ mr: 1, fontSize: "18px" }} />
                    Edit
                </MenuItem>
                <MenuItem 
                    onClick={() => handleDeleteReason(selectedReason?.id)}
                    disabled={deleting}
                    sx={{ 
                        color: deleting ? "#999999" : "#ff4444", 
                        "&:hover": { background: "#FFFFFF10" } 
                    }}
                >
                    <DeleteIcon sx={{ mr: 1, fontSize: "18px" }} />
                    {deleting ? "Deleting..." : "Delete"}
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default CommentReasons;